import React from 'react';
import { Lightbulb, Users, TrendingUp, Globe } from 'lucide-react';

const LeadershipPhilosophy = () => {
  const principles = [
    {
      icon: <Lightbulb size={32} />,
      title: 'Inclusive Excellence',
      description: 'Every child can achieve at the highest levels when provided with appropriate support, high expectations, and evidence-based interventions. True inclusion means ensuring all students, regardless of background or need, access rigorous, challenging curricula that prepares them for success in a globalized world.'
    },
    {
      icon: <Users size={32} />,
      title: 'Servant Leadership',
      description: 'Leadership is not about position or authority—it is about service. My military background taught me that effective leaders empower their teams, remove barriers, and model the values they expect. I lead from the front, whether delivering meals to families or implementing whole-school strategic improvement plans.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Data-Driven Decision Making',
      description: 'Strategic school improvement requires rigorous analysis of student outcomes, staff effectiveness, and operational efficiency. I use assessment data, stakeholder feedback, and research-informed practice to drive continuous improvement while maintaining the humanity and care that makes schools communities, not just institutions.'
    },
    {
      icon: <Globe size={32} />,
      title: 'Global Mindedness',
      description: 'Having worked across three continents, I understand that international school leadership demands cultural intelligence, adaptability, and a commitment to developing globally competent learners. Education must prepare students not just for their next school, but for their role as ethical, informed citizens in an interconnected world.'
    }
  ];

  return (
    <section id="leadership-philosophy" className="philosophy-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Leadership Philosophy & Vision</h2>
          <p className="section-subtitle">Building inclusive, high-performing learning communities through strategic leadership and unwavering commitment to every child's success</p>
        </div>

        <div className="vision-statement">
          <h3>My Vision for Educational Leadership</h3>
          <p>
            I believe great schools are built on three pillars: <strong>academic excellence without exception</strong>, <strong>genuine inclusion for every learner</strong>, and <strong>a culture of care that extends beyond the school gates</strong>. As a school leader, my mission is to create learning environments where rigorous expectations meet compassionate support, where data informs decisions but relationships drive change, and where every member of the community—students, staff, and families—feels valued, challenged, and empowered to achieve their potential.
          </p>
          <p>
            My approach integrates strategic planning with operational excellence, ensuring that school improvement is not merely aspirational but systematically implemented, rigorously monitored, and demonstrably effective. Whether managing budgets, leading curriculum transformation, or building safeguarding cultures, I bring discipline, innovation, and an unwavering focus on outcomes.
          </p>
        </div>

        <div className="principles-grid">
          {principles.map((principle, index) => (
            <div key={index} className="principle-card">
              <div className="principle-icon">
                {principle.icon}
              </div>
              <h4>{principle.title}</h4>
              <p>{principle.description}</p>
            </div>
          ))}
        </div>

        <div className="leadership-approach">
          <h3>What I Bring to International School Leadership</h3>
          <div className="approach-grid">
            <div className="approach-item">
              <h4>Strategic Vision</h4>
              <p>Experience developing and executing multi-year school improvement strategies aligned with international standards and accreditation requirements.</p>
            </div>
            <div className="approach-item">
              <h4>Financial Stewardship</h4>
              <p>Proven track record managing school budgets, optimising resource allocation, and ensuring financial sustainability while maintaining educational quality.</p>
            </div>
            <div className="approach-item">
              <h4>Change Management</h4>
              <p>Skilled in leading organisational transformation, from establishing specialist provisions to implementing new curriculum frameworks, always bringing staff along the journey.</p>
            </div>
            <div className="approach-item">
              <h4>Stakeholder Relations</h4>
              <p>Adept at building trust with boards, parents, staff, and students across diverse cultural contexts, ensuring transparent communication and collaborative decision-making.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadershipPhilosophy;
